import { useEffect, useState } from "react";

import useBidStore from "@/stores/Bid";
import { useLoadingStore } from "@/stores/Loading";

import {
  DefaultBidSearch,
  DefaultBidSearchKeywordList,
  DomesticTabData,
  getSearchConditionFilterToString,
  getSearchFilterToString,
  ProjectType,
} from "@/consts/Bid";

import { BidContent, GetBidListRequest } from "@/types/bid";
import { SearchConditionItem } from "@/types/setting";

import { isNotEmpty } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import { GetBidList } from "@/services/Bid";
import { GetSearchConditionList, SetSearchCondition } from "@/services/Setting";

import BidList from "@/pages/Bid/List";

import ContentLayout from "@/layouts/ContentLayout";

import CommonTab from "@/components/Common/Tab";
import BidSearch from "@/components/BidSearch";

export default function BidSpecification() {
  const { startLoading, endLoading } = useLoadingStore();

  const storedFilter = useBidStore((state) => state.filter);
  const setStoredFilter = useBidStore((state) => state.setFilter);
  const storedPage = useBidStore((state) => state.procurePage);
  const setStoredPage = useBidStore((state) => state.setProcurePage);
  const storedResult = useBidStore((state) => state.procureResult);
  const setStoredResult = useBidStore((state) => state.setProcureResult);

  const [searchConditionList, setSearchConditionList] = useState<
    SearchConditionItem[]
  >([]);
  const [bidContent, setBidContent] = useState<BidContent>();

  const handleSearch = async (
    toastFinish: boolean = false,
    newFilter?: GetBidListRequest
  ) => {
    try {
      startLoading("bid-spec-search");
      const targetFilter = newFilter ?? storedFilter;
      const searchKeywordString = getSearchFilterToString(targetFilter);
      const result = await GetBidList({ input: searchKeywordString });
      if (result.data.success) {
        setBidContent(result.data.pods[0].content);
        setStoredResult(result.data.pods[0].content);
        setStoredPage(storedFilter.settings.page);
        if (toastFinish) showToast({ message: "공고 검색이 완료되었습니다." });
      } else {
        // TODO:: Error
      }
    } catch (e) {
    } finally {
      endLoading("bid-spec-search");
    }
  };

  const handleSaveSearchCondition = async () => {
    try {
      if (!isNotEmpty(storedFilter.search_id)) {
        showToast({ message: "검색 조건을 만들어주세요." });
        return;
      }
      const params = getSearchConditionFilterToString(storedFilter);
      const result = await SetSearchCondition(params);
      if (result.data.success) {
        showToast({
          message: "검색 조건이 저장되었습니다.",
        });
        getSearchConditionList(false);
      } else {
        showToast({
          type: "error",
          message: `검색 조건 저장을 실패했습니다. ${result.data.error.content}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `검색 조건 저장을 실패했습니다. ${e}`,
      });
    } finally {
    }
  };

  const handleChangeSearchCondition = (
    targetConditionList: SearchConditionItem[],
    targetConditionId?: string
  ) => {
    if (isNotEmpty(targetConditionId)) {
      setStoredFilter(null);
    }
    const targetFilter =
      targetConditionId || !isNotEmpty(storedFilter?.search_id)
        ? null
        : storedFilter;
    const targetSearchCondition = targetFilter ??
      (targetConditionId
        ? targetConditionList.filter(
            (condition) => condition.search_id === targetConditionId
          )[0]
        : targetConditionList.filter((item) => item.pinned)[0]) ??
      targetConditionList[0] ?? {
        settings: DefaultBidSearch,
      };
    const {
      settings: { keyword_sets, ...otherSettings },
    } = targetSearchCondition;
    setStoredFilter({
      ...targetSearchCondition,
      settings: {
        ...otherSettings,
        page: storedPage,
        page_size: DefaultBidSearch.page_size,
        project_type: ProjectType.Procure,
        keyword_sets: DefaultBidSearchKeywordList.map(
          (keyword, keywordIndex) => keyword_sets[keywordIndex] ?? keyword
        ),
      },
    });
  };
  const getSearchConditionList = async (refresh: boolean) => {
    try {
      const result = await GetSearchConditionList();
      if (result.data.success) {
        const resultItem = result.data.pods[0].content.items;
        setSearchConditionList(resultItem);
        // 먼저 생성한 검색 조건이 없는 경우엔 무조건 refresh
        if (refresh || !isNotEmpty(storedFilter?.search_id)) {
          handleChangeSearchCondition(resultItem);
        }
      }
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    getSearchConditionList(true);
    if (isNotEmpty(storedResult)) {
      setBidContent(storedResult);
    }
  }, []);

  return (
    <ContentLayout>
      <CommonTab variants="string" data={DomesticTabData} />
      <BidSearch
        projectType={ProjectType.Procure}
        onSearch={handleSearch}
        totalItems={bidContent?.count}
        searchConditionList={searchConditionList}
        onChangeCondition={(searchId) => {
          handleChangeSearchCondition(searchConditionList, searchId);
        }}
        onSaveSearchCondition={handleSaveSearchCondition}
        onRefreshSearchCondition={() => getSearchConditionList(false)}
      >
        {bidContent && (
          <BidList
            content={bidContent}
            onSearch={handleSearch}
            projectType={storedFilter.settings.project_type}
          />
        )}
      </BidSearch>
    </ContentLayout>
  );
}
